import React, { useEffect, useMemo, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faChevronDown,
  faCheck,
  faTableCells,
  faFilter,
  faChevronLeft,
  faChevronRight,
  faFaceFrown,
} from "@fortawesome/free-solid-svg-icons";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const YEARS = ["2026", "2025", "2024"];

function parseEventDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const lower = dateStr.toLowerCase();
  const monthIndex = MONTHS.map((m) => m.toLowerCase()).findIndex((m) =>
    lower.includes(m),
  );
  const yearMatch = dateStr.match(/(20\d{2})/);
  const dayMatch = dateStr.match(/\b([0-3]?\d)\b/);
  const year = yearMatch ? Number(yearMatch[1]) : NaN;
  const day = dayMatch ? Number(dayMatch[1]) : 1;
  if (monthIndex === -1 || !year) return null;
  const jsDate = new Date(year, monthIndex, day);
  return { year, monthIndex, day, jsDate };
}

// Custom Dropdown Component for Year only
const CustomDropdown = ({ value, options, onChange, icon, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = value;

  return (
    <div ref={dropdownRef} className="relative w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 rounded-lg px-6 py-2.5 border border-gray-300 transition-all duration-200 group h-full w-full"
      >
        <FontAwesomeIcon icon={icon} className="w-4 h-4 text-gray-700" />
        <span className="text-sm font-semibold text-gray-700">
          {selectedLabel}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`w-4 h-4 text-gray-700 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-30 max-h-64 overflow-y-auto">
          {options.map((opt) => {
            const optValue = typeof opt === "object" ? opt.value : opt;
            const optLabel = typeof opt === "object" ? opt.label : opt;
            const isSelected = value === optValue;

            return (
              <button
                key={optValue}
                onClick={() => {
                  onChange(optValue);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                  isSelected
                    ? "bg-blue-50 text-blue-700 font-semibold"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{optLabel}</span>
                  {isSelected && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="w-4 h-4 text-blue-600"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const EventsPage = () => {
  const [eventsData, setEventsData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const autoSlideRef = useRef(null);

  const now = new Date();
  const defaultYear = String(now.getFullYear());

  const [yearFilter, setYearFilter] = useState(
    YEARS.includes(defaultYear) ? defaultYear : YEARS[0],
  );
  const [allMode, setAllMode] = useState(true);
  const [monthFilter, setMonthFilter] = useState(null);

  useEffect(() => {
    fetch("/data/events.json")
      .then((r) => r.json())
      .then((data) => {
        const withMeta = (data || [])
          .map((e) => {
            const meta = parseEventDate(e.date);
            return meta ? { ...e, __meta: meta } : null;
          })
          .filter(Boolean);
        withMeta.sort((a, b) => b.__meta.jsDate - a.__meta.jsDate);
        setEventsData(withMeta);
      })
      .catch((err) => {
        console.error("Error fetching events:", err);
        setEventsData([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (allMode) return eventsData;
    return eventsData.filter((e) => {
      const yOk = String(e.__meta.year) === String(yearFilter);
      const mOk =
        typeof monthFilter === "number"
          ? e.__meta.monthIndex === monthFilter
          : true;
      return yOk && mOk;
    });
  }, [eventsData, yearFilter, monthFilter, allMode]);

  const openEventPopup = (event) => {
    const images =
      Array.isArray(event.images) && event.images.length > 0
        ? event.images
        : [event.thumbnail || "https://placehold.co/600x400"];
    setSelectedEvent({ ...event, images });
    setCurrentImageIndex(0);
    document.body.style.overflow = "hidden";
  };

  const closeEventPopup = () => {
    setSelectedEvent(null);
    document.body.style.overflow = "auto";
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current);
      autoSlideRef.current = null;
    }
  };

  useEffect(() => {
    if (
      !selectedEvent ||
      !selectedEvent.images ||
      selectedEvent.images.length <= 1
    )
      return;
    if (autoSlideRef.current) clearInterval(autoSlideRef.current);
    autoSlideRef.current = setInterval(() => {
      setCurrentImageIndex((prev) =>
        prev === selectedEvent.images.length - 1 ? 0 : prev + 1,
      );
    }, 2500);
    return () => {
      if (autoSlideRef.current) {
        clearInterval(autoSlideRef.current);
        autoSlideRef.current = null;
      }
    };
  }, [selectedEvent]);

  const nextSlide = () => {
    if (selectedEvent) {
      setCurrentImageIndex((prev) =>
        prev === selectedEvent.images.length - 1 ? 0 : prev + 1,
      );
    }
  };

  const prevSlide = () => {
    if (selectedEvent) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedEvent.images.length - 1 : prev - 1,
      );
    }
  };

  const EventCard = ({ event }) => (
    <div
      className="event-card rounded-xl overflow-hidden shadow-md bg-white flex flex-col cursor-pointer border-2 p-1 border-white w-full"
      onClick={() => openEventPopup(event)}
    >
      <div className="relative h-[300px] max-h-[300px] w-full overflow-hidden rounded-lg">
        <img
          src={event.thumbnail || "https://placehold.co/600x400"}
          alt={event.title}
          className="w-full h-full"
          loading="lazy"
        />
      </div>
      <div className="p-2 text-center relative z-[5]">
        <p className="text-gray-800 font-semibold event-card-title text-sm leading-tight line-clamp-3 mb-2">
          {event.title}
        </p>
      </div>
      <div className="hover-text text-white">VIEW DETAILS</div>
    </div>
  );

  return (
    <>
      <style jsx="true">{`
        .event-card {
          transition:
            transform 0.4s ease,
            border 0.3s ease,
            box-shadow 0.3s ease;
          position: relative;
          overflow: hidden;
          height: 380px;
          width: 100%;
        }
        .event-card:hover {
          transform: scale(1.05);
          border: #e4ba14 7px solid !important;
          z-index: 10;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }
        .event-card:hover::after {
          content: "";
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.85);
          z-index: 1;
          pointer-events: none;
        }
        .event-card:hover .event-card-title {
          color: black;
          position: relative;
          z-index: 0;
        }
        .event-card img {
          transition: transform 0.3s ease;
          width: 100%;
          height: 100%;
          object-fit: fill !important;
        }
        .hover-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 14px;
          padding: 10px 20px;
          white-space: normal;
          text-align: center;
          max-width: 90%;
          opacity: 0;
          transition:
            opacity 0.3s ease,
            transform 0.3s ease;
          z-index: 2;
          text-transform: uppercase;
          font-weight: bold;
          pointer-events: none;
          border: 2px solid #e4ba14;
          border-radius: 4px;
          background-color: rgba(228, 186, 20, 0.2);
          letter-spacing: 1px;
        }
        .event-card:hover .hover-text {
          opacity: 1;
          transform: translate(-50%, -50%);
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* Header */}
      <div className="bg-gradient-to-r from-[#0056b3] to-[#2081e9] text-white mt-24"></div>
      <div className="bg-gradient-to-r from-[#0056b3] to-[#2081e9] text-white py-10 text-center shadow-md relative">
        <h1 className="text-4xl font-bold tracking-wider uppercase">
          Academic Enrichment Activities Glimpses
        </h1>
        <p className="text-lg opacity-90">
          Workshops, seminars, and hands-on activities to strengthen skills and
          enrich academic growth.
        </p>
      </div>

      {/* Modern Filter Toolbar with Month Tabs */}
      <div className="bg-white border-b shadow-sm top-0 z-20">
        <div className="py-3 container">
          {/* Year Filter and All Events Button */}
          <div className="container flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 mb-4">
            <button
              onClick={() => {
                setAllMode(true);
                setMonthFilter(null);
              }}
              className={`group relative inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 w-full sm:w-auto ${
                allMode
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              }`}
            >
              <FontAwesomeIcon icon={faTableCells} className="w-4 h-4" />
              <span>All Events</span>
              {allMode && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
              )}
            </button>

            <div className="w-full sm:w-auto">
              <CustomDropdown
                value={yearFilter}
                options={YEARS.map((y) => ({ value: y, label: y }))}
                onChange={(val) => {
                  setYearFilter(val);
                  setAllMode(false);
                }}
                icon={faCalendar}
                label="Year"
              />
            </div>
          </div>

          {/* Month Tabs - Grid Layout */}
          <div className="container">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-2">
              {MONTHS.map((month, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setMonthFilter(idx);
                    setAllMode(false);
                  }}
                  className={`px-3 py-2.5 rounded-lg font-medium text-xs sm:text-sm whitespace-nowrap transition-all duration-200 ${
                    !allMode && monthFilter === idx
                      ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md transform scale-105"
                      : "bg-gray-100 text-gray-700 hover:bg-[#e1e1e1] border border-gray-300"
                  }`}
                >
                  {month}
                </button>
              ))}
            </div>
          </div>

          {/* Active Filter Indicator */}
          {!allMode && (
            <div className="container mt-3 flex items-center gap-2 text-xs text-gray-600">
              <FontAwesomeIcon icon={faFilter} className="w-3.5 h-3.5" />
              <span className="font-medium">Filtered by:</span>
              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-semibold">
                {monthFilter !== null
                  ? `${MONTHS[monthFilter]} ${yearFilter}`
                  : yearFilter}
              </span>
              <span className="text-gray-500">({filtered.length} events)</span>
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <section className="py-12 bg-[#e1e1e1]">
        <div className="container mx-auto px-4 max-w-7xl">
          {loading ? (
            <div className="text-center text-gray-600 text-xl py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              Loading events...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <FontAwesomeIcon
                icon={faFaceFrown}
                className="w-16 h-16 text-gray-400 mx-auto mb-4"
              />
              <p className="text-gray-600 text-xl font-medium">
                No events found for this month
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Try adjusting your filters or view all events
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 justify-items-center">
              {filtered.map((event, idx) => (
                <EventCard key={event.id || idx} event={event} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Popup (card style) */}
      {selectedEvent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-2"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.9)" }}
        >
          <div className="bg-[#e1e1e1] rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative shadow-2xl">
            <button
              onClick={closeEventPopup}
              className="absolute top-6 right-6 bg-gray-100 hover:bg-red-600 hover:text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold text-gray-700 transition-all duration-200 z-10"
            >
              ×
            </button>
            <div className="flex flex-col lg:flex-row min-h-[500px]">
              <div className="lg:w-1/2 p-2 flex flex-col justify-center">
                <div className="relative bg-[#e1e1e1] overflow-hidden h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px] mb-4">
                  <img
                    loading="lazy"
                    src={selectedEvent.images[currentImageIndex]}
                    alt={selectedEvent.title}
                    className="w-full h-full rounded-xl object-contain object-center"
                  />
                  {selectedEvent.images.length > 1 && (
                    <>
                      <button
                        onClick={prevSlide}
                        className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/50 hover:bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition"
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          className="w-5 h-5"
                        />
                      </button>
                      <button
                        onClick={nextSlide}
                        className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/50 hover:bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition"
                      >
                        <FontAwesomeIcon
                          icon={faChevronRight}
                          className="w-5 h-5"
                        />
                      </button>
                    </>
                  )}
                </div>
                {selectedEvent.images.length > 1 && (
                  <div className="flex justify-center gap-2">
                    {selectedEvent.images.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-2.5 h-2.5 rounded-full transition ${
                          idx === currentImageIndex
                            ? "bg-blue-600 w-8"
                            : "bg-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="lg:w-1/2 p-8">
                <div className="mb-6">
                  <span className="inline-block bg-blue-100 text-blue-700 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                    📅 {selectedEvent.date}
                  </span>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    {selectedEvent.title}
                  </h2>
                </div>
                <div className="prose prose-lg">
                  <p className="text-gray-600 text-justify leading-relaxed whitespace-pre-line">
                    {selectedEvent.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventsPage;
